import { EyeOutlined, LinkOutlined } from "@ant-design/icons";
import { Badge, Button, Collapse, Descriptions, Tag } from "antd";
import moment from "moment";
import React from "react";
import { NavLink } from "react-router-dom";
import { CustomerLien } from "../helpers/shared-interfaces";
import { formatter } from "../helpers/utilities";
import { tabPaneStyle } from "../helpers/constants";

const { Panel } = Collapse;

interface Props {
  customerLien: CustomerLien;
}

export const CustomerLienView: React.FC<Props> = ({ customerLien }) => {
  const calculateDuration = (): number | null => {
    if (customerLien.startDate && customerLien.endDate) {
      return moment(customerLien.endDate, "YYYY-MM-DD").diff(
        moment(customerLien.startDate, "YYYY-MM-DD"),
        "months",
      );
    }
    return null;
  };

  const calculateEmployeeTotal = (): number => {
    return (
      (customerLien.employeePreTax || 0) + (customerLien.employeePostTax || 0)
    );
  };

  const calculateEmployerTotal = (): number => {
    return (
      (customerLien.employerPreTax || 0) + (customerLien.employerPostTax || 0)
    );
  };

  const getLastAuditInfo = (): JSX.Element | null => {
    if (customerLien.changeLogs && customerLien.changeLogs.length > 0) {
      const lastAudit = customerLien.changeLogs[0];
      return (
        <span>
          {lastAudit.user} - {lastAudit.action} on{" "}
          {moment(lastAudit.createdAt, "YYYY-MM-DD HH:mm:ss").format(
            "DD/MM/YYYY HH:mm",
          )}
        </span>
      );
    }
    return null;
  };

  return (
    <Collapse
      style={{ marginTop: "50px", marginBottom: "50px" }}
    >
      <Panel
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>
              Customer Lien <Badge count={1} style={tabPaneStyle} />
            </span>
            <Tag color={customerLien.loanPaid ? "#55b359" : "#ff5252"}>
              {customerLien.loanPaid ? "Paid" : "Not Paid"}
            </Tag>
          </div>
        }
        key={"CustomerLien"}
      >
        <Descriptions column={3}>
          {customerLien.provider && (
            <Descriptions.Item label={<b>Provider</b>}>
              {customerLien.provider}
            </Descriptions.Item>
          )}
          {customerLien.company && (
            <Descriptions.Item label={<b>Client</b>}>
              <NavLink to={`/companies/${customerLien.company.id}`}>
                {customerLien.company.name}
              </NavLink>
            </Descriptions.Item>
          )}
          {customerLien.loanRequest && (
            <Descriptions.Item label={<b>Associated Loan</b>}>
              <NavLink to={`/loan-requests/${customerLien.loanRequest.id}`}>
                <Button type="link" size="small" icon={<EyeOutlined />}>
                  View Request
                </Button>
              </NavLink>
            </Descriptions.Item>
          )}

          <Descriptions.Item label={<b>Current Amount</b>}>
            {formatter.format(customerLien.currentAmount)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Total Amount</b>}>
            {formatter.format(customerLien.totalAmount)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Outstanding</b>}>
            {formatter.format(
              customerLien.totalAmount - customerLien.currentAmount,
            )}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Employee Pre-Tax</b>}>
            {formatter.format(customerLien.employeePreTax || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Employee Post-Tax</b>}>
            {formatter.format(customerLien.employeePostTax || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Employee Total</b>}>
            {formatter.format(calculateEmployeeTotal())}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Employer Pre-Tax</b>}>
            {formatter.format(customerLien.employerPreTax || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Employer Post-Tax</b>}>
            {formatter.format(customerLien.employerPostTax || 0)}
          </Descriptions.Item>
          <Descriptions.Item label={<b>Employer Total</b>}>
            {formatter.format(calculateEmployerTotal())}
          </Descriptions.Item>

          {customerLien.startDate && (
            <Descriptions.Item label={<b>Start Date</b>}>
              {moment(customerLien.startDate, "YYYY-MM-DD").format(
                "DD/MM/YYYY",
              )}
            </Descriptions.Item>
          )}
          {customerLien.endDate && (
            <Descriptions.Item label={<b>End Date</b>}>
              {moment(customerLien.endDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
            </Descriptions.Item>
          )}
          {calculateDuration() !== null && (
            <Descriptions.Item label={<b>Duration</b>}>
              {calculateDuration()} months
            </Descriptions.Item>
          )}

          {customerLien.lienAttachmentUrl && (
            <Descriptions.Item label={<b>Document</b>} span={3}>
              <a
                href={customerLien.lienAttachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="small" icon={<LinkOutlined />}>
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {getLastAuditInfo() && (
            <Descriptions.Item label={<b>Last Updated</b>} span={3}>
              {getLastAuditInfo()}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Panel>
    </Collapse>
  );
};
